import React, { useState, useEffect } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';

export type TimelineViewDetailLevel = 'year' | 'month' | 'day' | 'second';

export interface TimelineViewState {
  textFilter: string;
  selectedItemTypes: string[];
  selectedTags: string[];
  excludedTags?: string[];
  selectedDocumentId?: string;
  groupBy: 'none' | 'type' | 'tag' | 'chapter' | 'storyline' | 'scene' | 'document';
  groupByTypes: string[];
  groupByTags: string[];
  groupByDocuments?: string[];
  viewMode: 'timeline' | 'table';
  timelineWindow?: { start: string; end: string };
  detailLevel?: TimelineViewDetailLevel | null;
}

interface SaveTimelineViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  state: TimelineViewState;
  onSuccess: () => void;
}

export const SaveTimelineViewModal: React.FC<SaveTimelineViewModalProps> = ({
  isOpen,
  onClose,
  projectId,
  state,
  onSuccess,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setError(null);
      setIsSubmitting(false);
      setConfirmOverwrite(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('timeline_view_name_required'));
      return;
    }
    if (trimmedName.toLowerCase() === 'timeline') {
      setError(t('timeline_view_name_invalid'));
      return;
    }
    if (!token) {
      setError(t('not_authenticated'));
      return;
    }

    if (!confirmOverwrite) {
      const viewsRes = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-views`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (viewsRes.ok) {
        const { views } = await viewsRes.json();
        const exists = views?.some((v: { name: string }) => v.name.toLowerCase() === trimmedName.toLowerCase());
        if (exists) {
          setConfirmOverwrite(true);
          setError(null);
          return;
        }
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-views`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmedName, state }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || t('timeline_view_save_failed'));
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('timeline_view_save_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('timeline_view_save_title')}</DialogTitle>
          <DialogDescription>{t('timeline_view_save_description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="timeline-view-name">
                {t('Name')} *
              </label>
              <Input
                id="timeline-view-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                disabled={isSubmitting}
                placeholder={t('timeline_view_name_placeholder')}
                autoFocus
              />
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {confirmOverwrite && (
              <Alert>
                <AlertDescription>{t('timeline_view_overwrite_warning')}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <CancelButton type="button" onClick={onClose} disabled={isSubmitting} />
            <Button
              type="submit"
              disabled={isSubmitting}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {isSubmitting ? (
                t('saving')
              ) : (
                <>
                  {t('timeline_view_save_button')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
